import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Spin, Table } from 'antd';
import getColumns from '../../../UsersTable/getColumns';
import { selectDisplayColumnsForUsers } from '../../../../redux/store/ui/common';
import { selectAuth } from '../../../../redux/store/userData/user';
import { getFilteredColumns } from '../../../../utils';
import { makeT } from '../../../BulkActions/messages';

type Props = {
  loadingCreated: boolean;
  // the just-created users, fetched back from the list API
  createdUsers: any[];
  // server-reported created count; falls back to the fetched rows
  createdCount?: number;
};

// Step 3 of the bulk-create wizard ("Preview"): the users that were just
// created, in the main users table's own columns. Split out of
// BulkCreateUsersDrawer so it is presentational — it still reads the column
// selection from the store, exactly as BulkActions/steps/PreviewStep does.
const CreatePreviewStep: React.FC<Props> = ({
  loadingCreated,
  createdUsers,
  createdCount,
}) => {
  const intl = useIntl();
  const t = makeT(intl);
  const auth = useSelector(selectAuth);
  const displayColumns = useSelector(selectDisplayColumnsForUsers);

  // Same columns the main users table uses, honouring the admin's column
  // selection — mirrors BulkActions PreviewStep.
  const previewColumns = useMemo(() => {
    const cols = getColumns({ intl, selectedBrand: auth?.brands?.[0] });
    return getFilteredColumns(cols, displayColumns);
  }, [intl, auth, displayColumns]);

  if (loadingCreated) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Spin />
        <div className="ba-help" style={{ marginTop: 12 }}>
          {t('fleetadmin.bulk.create.preview.loading')}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="BulkCreateUsers-preview">
      <div className="ba-preview-head">
        <div>
          <div className="ba-preview-title">
            {t('fleetadmin.bulk.create.preview.title', {
              count: createdCount ?? createdUsers.length,
            })}
          </div>
          <div className="ba-preview-sub">
            {t('fleetadmin.bulk.create.preview.sub')}
          </div>
        </div>
      </div>
      {createdUsers.length > 0 && (
        <div className="ba-table-wrap">
          <Table
            rowKey={(r: any) => r.id}
            columns={previewColumns}
            dataSource={createdUsers}
            size="middle"
            scroll={{ y: 360, x: 'max-content' }}
            pagination={{
              position: ['bottomLeft'],
              defaultPageSize: 50,
              pageSizeOptions: ['50', '100', '150', '200'],
              showSizeChanger: true,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CreatePreviewStep;
